import { Grid, IconButton, styled, CircularProgress } from "@mui/material";
import { lighten } from "@mui/material/styles";
import MxIcons from "@rf-l1/MxIcons";
import MxImageViewer from "@rf-l1/file-viewer/MxImageViewer";
import {
  IconPointerFilled,
  IconPointerStar,
  IconStar,
  IconStarFilled,
  IconX
} from "@tabler/icons-react";
import MxUploadStatusIndicator from "@rf-l1/file/MxUploadStatusIndicator";
import React, { useEffect, useState } from "react";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import { useRoma } from "@rf-l1/roma/RomaContext";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import { HCatchServerError } from "@/src-refactor/modules/shared/components/refactor/helpers/cath-error-server";
import useAlert from "@rf-hooks/useAlert";

interface ItemPictureProps extends IFileData {
  index: number;
  deleteFile: (index: number) => void | Promise<void>;
}

const ItemPicture = React.forwardRef((props: ItemPictureProps, ref: any) => {
  const { uploadMethod, onDeleteFile, isDeletingAll, onDataChange, data } =
    useRoma();
  const [stateUpload, setStateUpload] = useState<"uploading" | "uploaded" | "error">(
    "uploaded"
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const { warningAlert } = useAlert();

  const performDelete = () => {
    try {
      setIsDeleting(true);
      const maybePromise = props.deleteFile(props.index);
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        toast
          .promise(maybePromise as Promise<any>, {
            loading: "Eliminando imagen...",
            success: "Imagen eliminada",
            error: (err: any) => HCatchServerError(err)
          })
          .then(() => onDeleteFile(props.index))
          .catch(() => {
            // mantener estado si falla
          })
          .finally(() => setIsDeleting(false));
      } else {
        onDeleteFile(props.index);
        setIsDeleting(false);
      }
    } catch (e) {
      // fallback de eliminación local
      onDeleteFile(props.index);
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    warningAlert({
      title: "Eliminar imagen",
      message: "¿Quieres eliminar esta imagen?",
      declineBtnMessage: "Cancelar",
      confirmBtnMessage: "Eliminar"
    }).then(res => {
      if (res.type.confirm) {
        performDelete();
      }
    });
  };

  const makePrincipal = () => {
    if (props.index === 0) {
      return;
    }

    const currentItems = Array.isArray(data) ? [...data] : [];
    if (!currentItems.length || props.index >= currentItems.length) {
      return;
    }

    const newItems = [...currentItems];
    const [selected] = newItems.splice(props.index, 1);
    newItems.unshift(selected);

    onDataChange(newItems, {
      event: "update",
      oldIndex: props.index,
      newIndex: 0
    });
  };

  const handleMakePrincipalClick = () => {
    warningAlert({
      title: "Imagen principal",
      message: "¿Deseas establecer esta imagen como principal?",
      declineBtnMessage: "Cancelar",
      confirmBtnMessage: "Hacer principal"
    }).then(res => {
      if (res.type.confirm) {
        makePrincipal();
      }
    });
  };

  const handleUpload = debounce(async () => {
    setStateUpload("uploading");
    await uploadMethod?.(props as any)
      ?.then(res => {
        setStateUpload("uploaded");
      })
      .catch(() => {
        setStateUpload("error");
      });
  }, 200);

  useEffect(() => {
    if (props.file) {
      handleUpload();
    }
    return () => {
      handleUpload.cancel();
    };
  }, []);

  return (
    <ContentItem
      container
      ref={ref}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      sx={{
        opacity: stateUpload === "error" || isDeleting || isDeletingAll ? 0.5 : 1,
        pointerEvents: isDeleting || isDeletingAll ? "none" : "auto"
      }}
    >
      <OptionsContents item xs={12} id="ContentOptions">
        <IndicatorBadge isPrincipal={props.index === 0}>
          {props.index === 0 ? (
            <PrincipalIndicator>
              <MxIcons size={16}>
                <IconStarFilled color="#ffd230" />
              </MxIcons>
              <span>Imagen principal</span>
            </PrincipalIndicator>
          ) : (
            props.index + 1
          )}
        </IndicatorBadge>

        <IconButton
          className="delete-button"
          test-id={`carrusel-delete-item-${props.index}`}
          onClick={handleDeleteClick}
        >
          <MxIcons size={16}>
            <IconX />
          </MxIcons>
        </IconButton>
      </OptionsContents>
      <Grid item xs={12}>
        <MxImageViewer
          src={props.url}
          variant="rounded"
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            maxHeight: 160,
            objectFit: "contain",
            border: "1px solid #ccc",
            borderRadius: 0.5
          }}
        />
      </Grid>
      <MxUploadStatusIndicator state={stateUpload} position="bottom-left" />
      {(isDeleting || isDeletingAll) && (
        <DeletingOverlay container alignItems="center" justifyContent="center">
          <CircularProgress size={28} thickness={4} />
        </DeletingOverlay>
      )}
      {props.index !== 0 && (
        <MakePrincipalButton
          type="button"
          className="make-principal-button"
          onClick={handleMakePrincipalClick}
          disabled={isDeleting || isDeletingAll}
        >
          <MxIcons size={16}>
            <IconStar color="white" />
          </MxIcons>
          Hacer principal
        </MakePrincipalButton>
      )}
      {/* <MainItem item xs={12} display={props.index != 0 ? "none" : "start"}>
        Imagen principal
      </MainItem> */}
      {/* <Grid item xs={12}>
        <MxFormL3TextAreaField
          maxRows={3}
          label="Descripción"
          name={`images_property.${props.index}.comment`}
          withreacthookform={1}
          rows={3}
        />
      </Grid> */}
    </ContentItem>
  );
});

export default ItemPicture;

const OptionsContents = styled(Grid)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "flex-start",
  height: "100%",
  maxHeight: 160,
  width: "97%",
  position: "absolute",
  top: 10,
  left: 5,
  zIndex: 1,
  display: "flex",
  "& .delete-button": {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    display: "none"
  },
  "&:hover .delete-button": {
    display: "flex"
  },
  "@media (hover: none)": {
    "& .delete-button": {
      display: "flex"
    }
  }
}));

const IndicatorBadge = styled("div", {
  shouldForwardProp: prop => prop !== "isPrincipal"
})<{ isPrincipal: boolean }>(({ theme, isPrincipal }) => ({
  minWidth: isPrincipal ? "auto" : 25,
  height: 25,
  borderRadius: "9999px",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: isPrincipal ? "center" : "center",
  padding: isPrincipal ? "0 12px" : 0,
  fontSize: isPrincipal ? 13 : 14,
  fontWeight: isPrincipal ? 600 : 500,
  gap: isPrincipal ? theme.spacing(0.5) : 0,
  pointerEvents: "none",
  lineHeight: 1
}));

const PrincipalIndicator = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6
}));

const MainItem = styled(Grid)(({ theme }) => ({
  position: "absolute",
  right: "0.1%",
  bottom: "5px",
  zIndex: 1,
  padding: "0.2rem 0.8rem",
  borderRadius: "10px 0 10px 0",
  color: theme.palette.common.white,
  fontSize: 14,
  fontWeight: 400,
  backgroundColor: theme.palette.primary.main,
  transition: "all 0.3s"
}));
// eliminado: indicador inline, reemplazado por MxUploadStatusIndicator

const ContentItem = styled(Grid)(({ theme }) => ({
  width: 270,
  height: 170,
  display: "flex",
  gap: "1rem",
  cursor: "grab",
  position: "relative",
  zIndex: 0,
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover .make-principal-button": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto"
    }
  },
  "@media (hover: none)": {
    "& .make-principal-button": {
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto"
    }
  }
}));

const DeletingOverlay = styled(Grid)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(255,255,255,0.6)",
  zIndex: 2
}));

const MakePrincipalButton = styled("button")(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  bottom: theme.spacing(1),
  border: "none",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  textDecoration: "none",
  fontWeight: 600,
  fontSize: 14,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "background-color 0.2s ease, opacity 0.2s ease",
  opacity: 1,
  visibility: "visible",
  pointerEvents: "auto",
  zIndex: 2,
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: 5,
  "&:hover:not(:disabled)": {
    backgroundColor: lighten(theme.palette.primary.main, 0.2)
  },
  "&:focus-visible": {
    outline: `2px solid ${lighten(theme.palette.primary.main, 0.4)}`,
    outlineOffset: 2
  },
  "&:disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    cursor: "not-allowed",
    textDecoration: "none"
  },
  "@media (hover: hover) and (pointer: fine)": {
    opacity: 0,
    visibility: "hidden",
    pointerEvents: "none"
  },
  [theme.breakpoints.down("md")]: {
    opacity: 1,
    visibility: "visible",
    pointerEvents: "auto"
  }
}));
